import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  /**
   * Grace period (ms) before a submenu actually closes after the pointer leaves,
   * so moving diagonally from the trigger toward the submenu content doesn't
   * get cut off mid-travel. Cancelled if the pointer re-enters in time.
   * @type {number}
   */
  #closeDelay = 600;

  /**
   * @type {number | null}
   */
  #closeTimer = null;

  connectedCallback() {
    super.connectedCallback();

    this.overflowMenu?.addEventListener('pointerleave', () => this.#deactivate());
    // on load, cache the max height of the submenu so you can use it in a translate
    this.#cacheMaxOverflowMenuHeight();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    this.#cleanupMutationObserver();
    this.#clearCloseTimer();
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    this.#cacheMaxOverflowMenuHeight();
    setHeaderMenuStyle();
  }, 100);

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    // Cancel any pending close from a previous pointerleave so re-entering
    // the trigger or its submenu in time keeps it open.
    this.#clearCloseTimer();

    this.dispatchEvent(new MegaMenuHoverEvent());

    if (!(event.target instanceof Element)) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      // Align the mega-menu's text/photo cluster so its left edge lines up
      // with the first nav item ("Home"), instead of floating centered.
      this.#alignMegaMenu(submenu);

      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
          requestAnimationFrame(() => {
            if (submenu.offsetHeight > 0) {
              this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, {childList: true, subtree: true});

      // Auto-disconnect after 500ms to prevent memory leaks
      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    const submenuHeight = submenu ? submenu.offsetHeight : 0;

    this.headerComponent?.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');
  };

  /**
   * Schedule deactivation of the active item after a short grace period,
   * so a brief pointer excursion while moving toward the submenu doesn't
   * close it prematurely.
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    const menu = findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node && event.relatedTarget.parentElement?.matches('[slot="overflow"]');

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) return;

    this.#scheduleDeactivate();
  }

  /**
   * Queue up the deactivation of the currently active item, cancellable via
   * #clearCloseTimer if the pointer re-enters before it fires.
   * @param {HTMLElement | null} [item]
   */
  #scheduleDeactivate = (item = this.#state.activeItem) => {
    this.#clearCloseTimer();
    this.#closeTimer = window.setTimeout(() => {
      this.#closeTimer = null;
      this.#deactivate(item);
    }, this.#closeDelay);
  };

  /**
   * Cancel any pending scheduled deactivation.
   */
  #clearCloseTimer = () => {
    if (this.#closeTimer !== null) {
      window.clearTimeout(this.#closeTimer);
      this.#closeTimer = null;
    }
  };

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;
    if (this.overflowHovered) return;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  /**
   * Caches the maximum height of all submenus for consistent animations
   * Stores the value in a CSS custom property for use in transitions
   */
  #cacheMaxOverflowMenuHeight() {
    const submenus = this.querySelectorAll('[ref="submenu[]"]');
    const maxHeight = Math.max(
      ...Array.from(submenus)
        .filter((submenu) => submenu instanceof HTMLElement)
        .map((submenu) => submenu.offsetHeight)
    );
    this.headerComponent?.style.setProperty('--submenu-max-height', `${maxHeight}px`);
  }

  /**
   * Shifts a featured-collections mega-menu's text/photo cluster (which is
   * sized to its own content, not the full submenu width) so the first
   * rendered letterform of its list ( the "A" in "All Heritage Gifts", say)
   * lines up with the first rendered letterform of the top-level nav (the
   * "H" in "Home"), matching the reference site's mega-menu alignment
   * instead of centering it under the full-width dropdown.
   * @param {HTMLElement} submenu
   */
  #alignMegaMenu(submenu) {
    const grid = /** @type {HTMLElement | null} */ (submenu.querySelector('.mega-menu__grid'));
    if (!grid) return;

    // Only content-sized (flex) mega-menus should be shifted; grid-based
    // layouts already span the full submenu width and would overflow.
    if (getComputedStyle(grid).display !== 'flex') {
      grid.style.marginLeft = '';
      return;
    }

    const homeLink = this.querySelector('.menu-list__link');
    const firstItemLink = grid.querySelector('.mega-menu__link');
    if (!homeLink || !firstItemLink) return;

    // Reset before measuring so repeated opens don't compound the offset.
    grid.style.marginLeft = '0px';

    const homeTextLeft = this.#textLeftEdge(homeLink);
    const itemTextLeft = this.#textLeftEdge(firstItemLink);
    if (homeTextLeft == null || itemTextLeft == null) return;

    grid.style.marginLeft = `${homeTextLeft - itemTextLeft}px`;
  }

  /**
   * Returns the precise left edge (viewport px) of the first rendered glyph
   * inside an element, ignoring the element's own padding/box edges — walks
   * to the deepest non-blank text node and measures a Range around it, so
   * alignment matches actual letterforms rather than box boundaries.
   * @param {Element} el
   * @returns {number | null}
   */
  #textLeftEdge(el) {
    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, {
      acceptNode: (node) => (node.textContent && node.textContent.trim() ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP),
    });
    const textNode = walker.nextNode();
    if (!textNode) return el.getBoundingClientRect().left;

    const range = document.createRange();
    range.selectNodeContents(textNode);
    return range.getBoundingClientRect().left;
  }

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
